// GraphQL Types
import { DateInput, PropertySort } from 'src/common/__generated__/api/types';

import { HotelDataByIds, HotelDataByIdsProps } from './hotel-data-by-ids';
import { HotelDataByCampaign, HotelDataByCampaignProps } from './hotel-data-campaign';
import { HotelDataBySearch, HotelDataBySearchProps } from './hotel-data-by-search';

// Helper
import { checkValue, dateHelper } from 'src/components/common/helper';

interface HotelDataFetchProps {
  hotelIds?: string;
  campaignId?: string;
  regionName?: string;
  regionId?: string;
  checkInDate?: string;
  checkOutDate?: string;
  priceMin?: number;
  priceMax?: number;
  sort?: PropertySort;
  adults?: number;
  starRating?: string;
  guestRating?: string;
  paginationSize?: number;
  pageNumber?: number;
  enableTripAttach?: boolean;
  displayPropertyCount?: boolean;
  enableLivePricing?: boolean;
  destinationId?: string;
  travelerType?: any;
  paymentType?: any;
  fetchQueryType?: 'useQuery' | 'useStore';
}

enum HotelDataFetchMethod {
  byCampaign = 'byCampaign',
  byHotelIds = 'byHotelIds',
  bySearch = 'bySearch',
}

const checkInBy14Day = dateHelper.addDate(14);
const checkOutBy16Day = dateHelper.addDate(16);
const getCheckInCheckOutDates = (checkInDate: string, checkOutDate: string) => {
  const checkInDateFormatted = dateHelper.dateFormat(checkInDate, checkInBy14Day);
  let checkOutDateFormatted = dateHelper.dateFormat(checkOutDate, checkOutBy16Day);

  // CheckOut Date is future date than the checkindate
  if (!dateHelper.isAfterDate(new Date(checkInDateFormatted), new Date(checkOutDateFormatted))) {
    const newDateForCheckOut = checkInDateFormatted;
    checkOutDateFormatted = dateHelper.setDate(newDateForCheckOut, 2);
  }

  const checkInDateFormattedInputFormat: DateInput = dateHelper.getDateWithInputFormat(checkInDateFormatted);
  const checkOutDateFormattedInputFormat: DateInput = dateHelper.getDateWithInputFormat(checkOutDateFormatted);

  return { checkInDateFormattedInputFormat, checkOutDateFormattedInputFormat };
};

const HotelDataFetch = (props: HotelDataFetchProps) => {
  const {
    hotelIds,
    campaignId,
    regionName,
    regionId,
    checkInDate,
    checkOutDate,
    priceMin,
    priceMax,
    adults,
    starRating,
    guestRating,
    sort,
    paginationSize = 24,
    pageNumber,
    enableTripAttach,
    displayPropertyCount,
    enableLivePricing,
    destinationId,
    travelerType,
    paymentType,
    fetchQueryType
  } = props;

  if (checkValue(hotelIds)) {
    const { checkInDateFormattedInputFormat, checkOutDateFormattedInputFormat } = getCheckInCheckOutDates(
      checkInDate,
      checkOutDate,
    );

    const fetchingMethod = fetchQueryType || 'useQuery';

    const hotelDataByIdsProps: HotelDataByIdsProps = {
      method: checkValue(fetchingMethod) ? fetchingMethod : 'useQuery',
      checkInDate: checkInDateFormattedInputFormat,
      checkOutDate: checkOutDateFormattedInputFormat,
      hotelIds: hotelIds?.split(','),
      adults,
    };

    const { data, error, loading } = HotelDataByIds(hotelDataByIdsProps);
    return { data, error, loading, method: HotelDataFetchMethod.byHotelIds };
  } else if (checkValue(campaignId)) {
    const hotelDataByCampaignProps: HotelDataByCampaignProps = {
      campaignId,
      pageNumber,
      enableTripAttach,
      displayPropertyCount,
      enableLivePricing,
      destinationId,
    };

    const { data, error, loading, fetchMore } = HotelDataByCampaign(hotelDataByCampaignProps);
    return { data, error, loading, fetchMore, method: HotelDataFetchMethod.byCampaign };
  } else if (checkValue(regionName) && (checkValue(regionId) || regionId === '')) {
    const { checkInDateFormattedInputFormat, checkOutDateFormattedInputFormat } = getCheckInCheckOutDates(
      checkInDate,
      checkOutDate,
    );

    const hotelDataBySearchProps: HotelDataBySearchProps = {
      regionName,
      regionId,
      priceMin,
      priceMax,
      sort,
      checkInDate: checkInDateFormattedInputFormat,
      checkOutDate: checkOutDateFormattedInputFormat,
      adults,
      starRating,
      guestRating,
      paginationSize,
      travelerType,
      paymentType,
    };

    const { data, error, loading } = HotelDataBySearch(hotelDataBySearchProps);

    return { data, error, loading, method: HotelDataFetchMethod.bySearch };
  } else {
    return null;
  }
};

export { HotelDataFetch, HotelDataFetchProps };
